import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, Phone } from "@phosphor-icons/react";
import { SITE, NAV } from "@/lib/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-white/80 backdrop-blur-sm"
      } border-b border-[var(--es-border)]`}
    >
      <div className="es-container flex items-center justify-between h-24 md:h-28 gap-4">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 flex-shrink-0">
          <img src={SITE.logo} alt="Empire Serv" className="h-20 md:h-24 lg:h-[104px] w-auto" />
          <span className="hidden md:flex flex-col leading-none border-l border-[var(--es-border)] pl-3">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--es-text-muted)] font-semibold">Facility</span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--es-text-muted)] font-semibold mt-1">Maintenance</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6" data-testid="main-navigation">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-semibold tracking-wide whitespace-nowrap transition-colors ${
                  isActive ? "text-[var(--es-accent)]" : "text-[var(--es-text)] hover:text-[var(--es-navy)]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          <a href={SITE.phoneHref} data-testid="header-phone-link" className="hidden xl:flex items-center gap-1.5 text-sm font-semibold text-[var(--es-navy)] whitespace-nowrap">
            <Phone size={16} weight="fill" />
            <span>{SITE.phone}</span>
          </a>
          <Link to="/quote" data-testid="header-quote-cta" className="es-btn-accent !py-2 !px-4 text-xs whitespace-nowrap">
            Request a Quote
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-[var(--es-navy)]"
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[var(--es-border)] bg-white">
          <div className="es-container py-4 grid gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-2 py-2.5 text-sm font-semibold border-b border-[var(--es-border)] last:border-0 ${
                    isActive ? "text-[var(--es-accent)]" : "text-[var(--es-text)]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/quote" data-testid="mobile-quote-cta" className="es-btn-accent mt-3">Request a Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
}
